// uiSound.js — THE SOUND KIT. 2026-08-31.
//
// Braeden: "does it all have noise sounds that are different so people can
// distinct the difference"
//
// It did not have ANY. Measured across the whole live surface: every `new
// Audio` in the app was her TTS or the microphone — there was not one UI sound,
// and not one sound asset on disk. Assist pointed, clicked, opened windows and
// finished tasks in total silence, so nothing that happened off to the side of
// where you were looking registered at all.
//
// WHY SYNTHESISED, NOT FILES. These are 60-200ms cues. Shipping eight audio
// files means eight fetches that can 404, a packaging step that can miss them,
// and a desktop app loading them over file://. Generated tones have none of
// that, are a few hundred bytes of code, and can be tuned by ear in one place.
//
// DISTINCTNESS IS THE POINT. His ask is that a person can tell the difference
// WITHOUT looking. So the kit is deliberately spread across three axes rather
// than being eight pitches of the same beep:
//
//   shape     sine = her own actions (soft, round)
//             triangle = something the SYSTEM did (brighter, harder edge)
//   direction rising = something opened / started / succeeded
//             falling = something closed / failed
//             flat = a neutral tick
//   register  point/click sit high and short so they never mask her voice;
//             done/error sit low and are the only two that ring at all.
//
// Nothing here plays over her TTS: `speaking` is checked by the caller via
// `setSpeaking()`, because a click tick landing in the middle of a spoken
// sentence is exactly the noise that makes people mute an app.

const STORE_KEY = 'aria_ui_sound';

let ctx = null;
let master = null;
let muted = false;
let speaking = false;

try { muted = localStorage.getItem(STORE_KEY) === 'off'; } catch (_) { /* private mode */ }

/** The context can only be created inside a gesture, so this is called lazily. */
function ac() {
  if (ctx) return ctx;
  try {
    const C = window.AudioContext || window.webkitAudioContext;
    if (!C) return null;
    ctx = new C();
    master = ctx.createGain();
    // Quiet on purpose. These are cues, not notifications.
    master.gain.value = 0.055;
    master.connect(ctx.destination);
  } catch (_) { ctx = null; }
  return ctx;
}

/**
 * One voice: a tone that slides from f0 to f1 and fades out.
 * The gain envelope is what stops a click: never start or end at a hard 0.
 */
function tone(f0, f1, ms, type, delay, gain) {
  const c = ac();
  if (!c) return;
  const t = c.currentTime + (delay || 0);
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type || 'sine';
  o.frequency.setValueAtTime(f0, t);
  if (f1 && f1 !== f0) o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + ms / 1000);
  const peak = gain == null ? 1 : gain;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + 0.012);          // quick, not instant
  g.gain.exponentialRampToValueAtTime(0.0001, t + ms / 1000);    // never to true zero
  o.connect(g); g.connect(master);
  o.start(t);
  o.stop(t + ms / 1000 + 0.02);
}

// ── the kit ───────────────────────────────────────────────────────────────
// Each entry says what it is FOR, so a new one gets added by meaning rather
// than by picking a pitch nobody has used yet.
// The kit is DATA, not closures, so "can a person tell these apart?" is a
// question that can be MEASURED instead of argued about. Each cue is a list of
// notes: [f0, f1, ms, shape, delayS, gain]. See uiSound.test for the check that
// no two cues are within the just-noticeable-difference of each other.
const KIT = {
  // she is pointing at something on your screen — highest and shortest in the
  // kit, so it reads as a tap on the glass rather than an alert
  point:  [[1180, 1180, 70, 'sine', 0, 0.7]],
  // she clicked it. Two ticks, second lower: the thing giving way
  click:  [[1320, 1320, 45, 'triangle', 0, 0.8], [880, 880, 55, 'triangle', 0.045, 0.6]],
  // a window or app came up — rising, because something arrived
  open:   [[520, 700, 110, 'sine', 0, 1], [700, 900, 120, 'sine', 0.08, 0.5]],
  // and went away. TWO steps down, where `stop` is one — a door closing rather
  // than a switch flipping. They measured 2.4 apart as identical single falls.
  close:  [[760, 600, 90, 'sine', 0, 0.6], [600, 440, 120, 'sine', 0.07, 0.5]],
  // the job is finished. The only warm interval in the kit (a major third), so
  // completion is the one thing that sounds like an arrival
  done:   [[587, 587, 130, 'sine', 0, 1], [740, 740, 190, 'sine', 0.1, 0.9]],
  // she needs an answer — rises at the end, the way a question does. Sits a
  // fifth ABOVE `done` on purpose: both are two-note rises, so register is the
  // only thing left to tell them apart (they measured 1.9 apart before).
  ask:    [[880, 880, 80, 'sine', 0, 0.7], [1040, 1180, 150, 'sine', 0.09, 0.7]],
  // something failed. Low and falling; the only cue that starts below 400Hz
  error:  [[300, 220, 220, 'triangle', 0, 0.9]],
  // the mic opened / closed — soft, never competing with the first word
  listen: [[440, 660, 130, 'sine', 0, 0.55]],
  stop:   [[660, 440, 130, 'sine', 0, 0.5]],
  // your message went — the quietest thing here
  send:   [[900, 1050, 60, 'sine', 0, 0.45]],
};

/** The recipe for a cue, for tests and for a settings preview. */
function spec(name) { return KIT[name] ? KIT[name].map((n) => n.slice()) : null; }

/**
 * Play a cue by name. Unknown names are ignored rather than throwing — a sound
 * may never be the reason a turn breaks.
 */
function play(name) {
  try {
    if (muted || speaking) return;
    const notes = KIT[name];
    if (!notes) return;
    const c = ac();
    if (!c) return;
    if (c.state === 'suspended') { c.resume().catch(() => {}); }
    for (const [f0, f1, ms, type, delay, gain] of notes) tone(f0, f1, ms, type, delay, gain);
  } catch (_) { /* never */ }
}

/** Her voice wins. Called around TTS playback so cues never talk over her. */
function setSpeaking(v) { speaking = !!v; }

function setMuted(v) {
  muted = !!v;
  try { localStorage.setItem(STORE_KEY, muted ? 'off' : 'on'); } catch (_) {}
}
function isMuted() { return muted; }

/** Names, for a settings list or a test harness. */
function names() { return Object.keys(KIT); }

export default { play, setSpeaking, setMuted, isMuted, names, spec, KIT };
export { play, setSpeaking, setMuted, isMuted, names, spec, KIT };
